import { Injectable } from '@angular/core';
import { CanvasInputMode, CanvasInputModeService } from './canvas-input-mode.service';
import { CanvasService } from './canvas.service';
import { SelectedElementsService } from './selected-elements.service';
import fitCurve from 'fit-curve';

type MouseFuction = (event: MouseEvent) => void;

@Injectable({ providedIn: 'root' })
export class FreeDrawService {
    private handleMouseDownFuction: MouseFuction;
    private handleMouseMoveFuction: MouseFuction;
    private handleMouseUpFuction: MouseFuction;

    rawLines = [];
    rawLinesData = [];
    fittedCurves = [];
    fittedCurvesData = [];
    error = 300;
    isMouseDown = false;

    newPathElement;

    constructor(
        private inputMode: CanvasInputModeService,
        private canvasService: CanvasService,
        private selectionService: SelectedElementsService
    ) {
        this.init = this.init.bind(this);
    }

    init() {
        this.bindMouseEvents();
    }

    destroy() {
        this.unBindMouseEvents();
    }

    bindMouseEvents() {
        this.handleMouseDownFuction = this.handleMouseDown.bind(this);
        this.handleMouseMoveFuction = this.handleMouseMove.bind(this);
        this.handleMouseUpFuction = this.handleMouseUp.bind(this);

        document.addEventListener('mousedown', this.handleMouseDownFuction);
        document.addEventListener('mousemove', this.handleMouseMoveFuction);
        document.addEventListener('mouseup', this.handleMouseUpFuction);
    }

    unBindMouseEvents() {
        document.removeEventListener('mousedown', this.handleMouseDownFuction);
        document.removeEventListener('mousemove', this.handleMouseMoveFuction);
        document.removeEventListener('mouseup', this.handleMouseUpFuction);
    }

    lineDataToPathString(lineData) {
        var str = '';
        lineData.map((xy, i) => {
            if (i == 0) {
                str += 'M ';
            } else {
                str += 'L ';
            }
            str += xy[0] + ' ' + xy[1] + ' ';
        });
        return str;
    }

    fittedCurveDataToPathString(fittedLineData) {
        var str = '';
        fittedLineData.map((bezier, i) => {
            if (i == 0) {
                str += 'M ' + bezier[0][0] + ' ' + bezier[0][1];
            }
            str +=
                'C ' +
                this.roundToNearestHalf(bezier[1][0]) +
                ' ' +
                this.roundToNearestHalf(bezier[1][1]) +
                ' ' +
                this.roundToNearestHalf(bezier[2][0]) +
                ' ' +
                this.roundToNearestHalf(bezier[2][1]) +
                ' ' +
                this.roundToNearestHalf(bezier[3][0]) +
                ' ' +
                this.roundToNearestHalf(bezier[3][1]) +
                ' ';
        });

        return str;
    }

    private roundToNearestHalf(num) {
        const rounded = Math.round(num * 2) / 2;
        return rounded % 1 === 0 ? rounded : parseFloat(rounded.toFixed(1));
    }

    updateLines(updateAllCurves = false) {
        let path;
        this.rawLinesData.forEach((lineData, i) => {
            this.newPathElement.setAttribute('d', this.lineDataToPathString(lineData));
            if (updateAllCurves) {
                if (this.fittedCurves.length <= i) {
                    path = document.createElementNS('SVG', 'path');
                    path.setAttribute('stroke', 'red');
                    this.fittedCurves.push(path);
                }
                if (lineData.length > 1) {
                    const newPath = fitCurve(lineData, this.error);
                    this.newPathElement.setAttribute('d', this.fittedCurveDataToPathString(newPath));
                }
            }
        });
    }
    private handleMouseDown() {
        if (this.inputMode.getMode() === CanvasInputMode.FREE_DRAW) {
            this.clearValues();
            const canvas = this.canvasService.getCanvas();
            const id = canvas.getNextId();
            const curShape = canvas.curShape;
            const strokeColor = this.canvasService.getStrokeColorForBackground();
            this.newPathElement = canvas.addSVGElementsFromJson({
                element: 'path',
                curStyles: true,
                attr: {
                    d: '',
                    id: id,
                    fill: '#ffffff',
                    stroke: strokeColor,
                    opacity: curShape.opacity,
                    'stroke-linecap': 'round',
                    type: CanvasInputMode.FREE_DRAW,
                },
            });
            this.newPathElement.setAttribute('stroke', strokeColor);
            canvas.addCommandToHistory(new canvas.history.InsertElementCommand(this.newPathElement));

            this.rawLinesData.push([]);
            this.isMouseDown = true;
        }
    }

    private handleMouseUp() {
        if (this.inputMode.getMode() === CanvasInputMode.FREE_DRAW && this.isMouseDown) {
            this.isMouseDown = false;
            this.updateLines(true);
            if (this.newPathElement) {
                this.selectionService.setSelectedElements([this.newPathElement]);
            }
            this.clearValues();
        }
    }

    private handleMouseMove(event) {
        if (this.inputMode.getMode() === CanvasInputMode.FREE_DRAW && this.isMouseDown) {
            let rootSctm = this.canvasService.getSCTM();
            let pt = this.canvasService.transformPoint(event.clientX, event.clientY, rootSctm);
            let mouseX = Number(pt.x);
            let mouseY = Number(pt.y);
            this.rawLinesData[this.rawLinesData.length - 1].push([mouseX, mouseY]);
            this.updateLines();
        }
    }

    private clearValues() {
        this.rawLinesData = [];
        this.rawLines = [];
        this.fittedCurves = [];
        this.fittedCurvesData = [];
        this.newPathElement = null;
    }
}
